import { Injectable, NgZone } from '@angular/core';
import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { Store } from '@ngrx/store';
import { BehaviorSubject, combineLatest, distinctUntilChanged, map, Observable, take } from 'rxjs';
import { UserSettingsService } from './user-settings.service';
import { UserSettingStore } from './user-setting-store.base';
import {
  coerceMenuLayout, EMPTY_LAYOUT, layoutFor, MENU_LAYOUT_KEY, MenuLayout, MenuLayoutPref, MenuProfile,
} from './menu-layout.util';

/** App-wide cache of the user's per-form-factor sidenav layout (order + hidden). Same reactive,
 *  server-synced, cross-tab shape as DateFormatService/NumberFormatService: the base owns the
 *  BroadcastChannel, the auth-gated per-identity load, and the optimistic write. This store adds
 *  viewport→profile resolution so the sidenav renders the layout matching the current screen. */
@Injectable({ providedIn: 'root' })
export class MenuLayoutService extends UserSettingStore {
  private readonly layout = new BehaviorSubject<MenuLayoutPref>({});
  readonly layout$: Observable<MenuLayoutPref> = this.layout.asObservable();

  // Assigned in the constructor (not as field initializers): these depend on the injected
  // `breakpoints`, and initializing them in the body — after super() and the parameter-property
  // assignment — avoids any reliance on field-initializer vs parameter-property ordering.
  readonly activeProfile$: Observable<MenuProfile>;
  readonly activeLayout$: Observable<MenuLayout>;

  constructor(
    userSettings: UserSettingsService,
    store: Store,
    zone: NgZone,
    private breakpoints: BreakpointObserver,
  ) {
    super(userSettings, store, zone);
    this.activeProfile$ = this.breakpoints
      .observe([Breakpoints.Handset, Breakpoints.Tablet, Breakpoints.Web])
      .pipe(
        // Real BreakpointObserver.observe() emits the CURRENT match synchronously on subscribe, so
        // combineLatest in activeLayout$ fires immediately without any seed. No startWith(): a
        // placeholder emission would be a real value a take(1)/snapshot consumer could observe
        // instead of the true profile on a non-desktop device.
        map(state => this.profileFor(state.breakpoints)),
        distinctUntilChanged(),
      );
    this.activeLayout$ = combineLatest([this.layout$, this.activeProfile$]).pipe(
      map(([pref, profile]) => layoutFor(pref, profile)),
    );
  }

  // Matches by substring rather than exact key lookup: CDK's real BreakpointObserver keys
  // `state.breakpoints` by each ATOMIC (comma-split) media query, which is itself always a
  // substring of the joined Breakpoints.X constant it came from — so checking "is this matched
  // key contained in Breakpoints.Handset/.Tablet" is a superset match that also tolerates a
  // partial/abbreviated query string (as used by simplified test doubles for BreakpointObserver).
  private profileFor(bp: { [query: string]: boolean }): MenuProfile {
    const matched = Object.keys(bp).filter(k => bp[k]);
    if (matched.some(k => Breakpoints.Handset.includes(k))) return 'mobile';
    if (matched.some(k => Breakpoints.Tablet.includes(k))) return 'tablet';
    return 'desktop';
  }

  private update(mutate: (pref: MenuLayoutPref) => MenuLayoutPref): void {
    const current = this.layout.value;
    const next = mutate(structuredClone(current));
    // Skip a no-op mutation (re-picking the current order, hiding an already-hidden id): no wasted
    // app-wide emission and no redundant HTTP write. The base setPref() guards this for string prefs;
    // this object pref needs its own structural compare.
    if (JSON.stringify(next) === JSON.stringify(current)) return;
    this.layout.next(next);
    // Pass the RAW object, not a pre-serialized string: UserSettingsService.set() JSON-encodes the
    // body exactly once. Pre-stringifying here would double-encode, so the value round-trips from the
    // backend as a JSON string that coerceMenuLayout() rejects — losing every saved layout on reload.
    this.persist(MENU_LAYOUT_KEY, next, Object.keys(next).length === 0);
  }

  private editProfile(pref: MenuLayoutPref, profile: MenuProfile): MenuLayout {
    const existing = pref[profile] ?? EMPTY_LAYOUT;
    const layout: MenuLayout = { order: [...existing.order], childOrder: { ...existing.childOrder }, hidden: [...existing.hidden] };
    pref[profile] = layout;
    return layout;
  }

  reorderModules(profile: MenuProfile, orderedIds: string[]): void {
    this.update(pref => {
      this.editProfile(pref, profile).order = [...orderedIds];
      return pref;
    });
  }

  reorderChildren(profile: MenuProfile, parentId: string, orderedIds: string[]): void {
    this.update(pref => {
      this.editProfile(pref, profile).childOrder[parentId] = [...orderedIds];
      return pref;
    });
  }

  setHidden(profile: MenuProfile, id: string, hidden: boolean): void {
    this.update(pref => {
      const layout = this.editProfile(pref, profile);
      const set = new Set(layout.hidden);
      if (hidden) set.add(id); else set.delete(id);
      layout.hidden = [...set];
      return pref;
    });
  }

  copyFrom(dst: MenuProfile, src: MenuProfile): void {
    this.update(pref => {
      pref[dst] = structuredClone(pref[src] ?? EMPTY_LAYOUT);
      return pref;
    });
  }

  resetProfile(profile: MenuProfile): void {
    this.update(pref => {
      delete pref[profile];
      return pref;
    });
  }

  resetAll(): void {
    if (Object.keys(this.layout.value).length === 0) return; // already default
    this.layout.next({});
    this.persist(MENU_LAYOUT_KEY, {}, true);
  }

  protected load(userId: number): void {
    this.userSettings
      .getAllSharedStrict(userId)
      .pipe(take(1))
      .subscribe({
        next: settings => this.emit(this.layout, coerceMenuLayout(settings[MENU_LAYOUT_KEY])),
        error: () => this.rearmLoad(userId),
      });
  }

  protected override handlesKey(key?: string): boolean {
    return key === MENU_LAYOUT_KEY;
  }

  protected onBroadcast(data: { key?: string; value?: unknown }): void {
    // The base broadcasts the RAW value we passed to persist() (structured-cloned over the channel),
    // i.e. the MenuLayoutPref object itself — or {} for a resetAll DELETE. Coerce it directly; there
    // is no JSON string to parse (that would be the double-encode this store deliberately avoids).
    if (data?.key === MENU_LAYOUT_KEY) {
      this.emit(this.layout, coerceMenuLayout(data.value));
    }
  }
}
